"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useCallback } from "react";
import { MdLocationOn } from "react-icons/md";
import type { GetLocationResponse } from "@/openapi/generated";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { useTenantData } from "@/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMediaAssetGet,
  usePresignedImageErrorRecovery,
} from "@/features/media/hooks";
import { formatLocationAddressSingleLine } from "@/lib/utils/formatLocationAddress";
import { resolveLocationCoverUrl } from "@/lib/utils/media/fetch-media-asset";
import { invalidateMediaAssetQueries } from "@/lib/utils/media/media-asset-query-key";
import { buildLocationPublicPath, appendFromListingQuery } from "@/lib/utils/publicPaths";
import { useThemeTokens } from "@/lib/theme/tokens";
import { ImageWithFallback } from "@/components/shared";
import { Card, Heading, Link, Text } from "@/components/ui";

export interface LocationListCardProps {
  location: GetLocationResponse;
  imageUrl?: string | null;
}

export const LocationListCard = ({ location, imageUrl }: LocationListCardProps) => {
  const theme = useThemeTokens();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();
  const locationId = Number(location.locationId);

  const { url: coverMediaUrl } = useMediaAssetGet(
    {
      tenantId,
      ownerType: EntryType.Location,
      ownerId: locationId,
      mediaGroup: MediaGroup.Cover,
    },
    {
      enabled: locationId > 0 && !!tenantId && imageUrl == null,
      requireAuth: false,
    },
  );
  const coverImageUrl = resolveLocationCoverUrl(imageUrl ?? coverMediaUrl);
  const refreshImage = useCallback(
    () =>
      invalidateMediaAssetQueries(
        queryClient,
        tenantId,
        EntryType.Location,
        locationId,
        MediaGroup.Cover,
      ),
    [queryClient, tenantId, locationId],
  );
  const handleCoverImageError = usePresignedImageErrorRecovery(
    coverImageUrl,
    refreshImage,
  );
  const href = appendFromListingQuery(
    buildLocationPublicPath({
      name: location.name || "location",
      locationId,
    }),
    "locations",
  );
  const addressLine = formatLocationAddressSingleLine(location);

  return (
    <Link href={href} display="block" h="100%" _hover={{ textDecoration: "none" }}>
      <Card
        p={0}
        overflow="hidden"
        h="100%"
        transition="all 0.2s ease"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              borderColor: theme.accent.primaryBorder,
              boxShadow: theme.shadow.cardFeatured,
              transform: "translateY(-2px)",
            },
          },
        }}
      >
        {coverImageUrl ? (
          <Box w="100%" aspectRatio={16 / 9} flexShrink={0}>
            <ImageWithFallback
              src={coverImageUrl}
              alt={location.name || "Location"}
              fallbackType="image"
              width="100%"
              height="100%"
              borderRadius="0"
              objectFit="cover"
              onImageError={handleCoverImageError}
            />
          </Box>
        ) : null}
        <Flex direction="column" gap={2} p={5}>
          <Flex align="center" gap={2}>
            <Box color={theme.accent.primary} fontSize="20px" aria-hidden flexShrink={0}>
              <MdLocationOn />
            </Box>
            <Heading size="sm" fontWeight="600" lineHeight="1.25" noOfLines={2}>
              {location.name}
            </Heading>
          </Flex>
          {addressLine ? (
            <Text variant="secondary" fontSize="sm" lineHeight="1.5" noOfLines={2}>
              {addressLine}
            </Text>
          ) : (
            <Text variant="tertiary" fontSize="sm" fontStyle="italic">
              View location details
            </Text>
          )}
        </Flex>
      </Card>
    </Link>
  );
};
